/**
 * Availability rules and geometry, pure and shared by BOTH sides.
 *
 * The server validates with the same function the entry form disables its Save
 * button with, so "the server refused it" and "the form said no" can never
 * disagree; and the lane packing that decides where a block is drawn is here
 * rather than in the component because it is the sort of thing a test should be
 * able to reach without a DOM.
 *
 * Free of node-only and browser-only imports, like shared/types.ts.
 */
import {
  AVAILABILITY_KINDS,
  AVAILABILITY_MAX_HOURS,
  AVAILABILITY_NOTE_MAX,
  HOUR_MS,
  snapToHour,
  type AvailabilityInput,
  type AvailabilityKind,
} from "./types.js";

/** What each kind reads as, and what it means, for a legend and a picker. */
export const AVAILABILITY_LABELS: Record<AvailabilityKind, { label: string; hint: string }> = {
  away: { label: "Away", hint: "Not working — holiday, sick, out" },
  busy: { label: "Busy", hint: "Working, but spoken for — a meeting, another project" },
  free: { label: "Free", hint: "Around outside the usual hours — ask away" },
};

/** A half-open span of time. Everything below works on this shape, not on whole entries. */
export interface Span {
  startMs: number;
  endMs: number;
}

/**
 * The span as it will actually be stored: both ends snapped down to the hour,
 * exactly as a task's schedule is. Snapping the END down too, rather than up,
 * is what makes "09:00 to 17:00" survive a round trip unchanged; a half hour
 * dragged onto the grid collapses to nothing and is caught by the check below
 * rather than silently becoming an hour nobody asked for.
 */
export function snapSpan(span: Span): Span {
  return { startMs: snapToHour(span.startMs), endMs: snapToHour(span.endMs) };
}

/**
 * Why this entry would be refused, or null when it is fine. Shared by the form
 * and the route, so the two can only ever say the same thing.
 */
export function availabilityProblem(input: Partial<AvailabilityInput>): string | null {
  if (!(AVAILABILITY_KINDS as readonly string[]).includes(input.kind as string)) {
    return `kind must be one of ${AVAILABILITY_KINDS.join(", ")}`;
  }
  if (!Number.isFinite(input.startMs) || !Number.isFinite(input.endMs)) return "start and end are required";
  const { startMs, endMs } = snapSpan({ startMs: input.startMs!, endMs: input.endMs! });
  if (endMs <= startMs) return "it has to last at least an hour";
  if ((endMs - startMs) / HOUR_MS > AVAILABILITY_MAX_HOURS) {
    return `one entry covers at most ${AVAILABILITY_MAX_HOURS / 24} days`;
  }
  if ((input.note ?? "").trim().length > AVAILABILITY_NOTE_MAX) {
    return `the note is longer than ${AVAILABILITY_NOTE_MAX} characters`;
  }
  // Needing Claude is something an absence does. Allowing it on a `free` span
  // would put "ask me anything this evening" on the list of work nobody is doing.
  if (input.needsClaude && input.kind === "free") return "a free span does not need Claude";
  return null;
}

/**
 * How long before a flagged span starts the runner primes Claude's five-hour
 * window, so the window resets just as the span begins. Mirrors
 * `WINDOW_HOURS` in runner/prime.ts, which is what actually sends the prompt.
 */
export const CLAUDE_PRIME_HOURS = 5;

/** How long before a "need Claude" entry starts the board's banner comes up. */
export const CLAUDE_BANNER_HOURS = 5;

/**
 * When the runner sends the priming prompt for a flagged span: CLAUDE_PRIME_HOURS
 * before it starts. The calendar draws a line there. It can land on the day
 * before the span, which is why the line is placed against the column, not the block.
 */
export function claudePrimeAt(span: Span): number {
  return span.startMs - CLAUDE_PRIME_HOURS * HOUR_MS;
}

/** Whether a span touches a window at all. Half-open at both ends, so touching edges do not count. */
export function overlaps(a: Span, b: Span): boolean {
  return a.startMs < b.endMs && a.endMs > b.startMs;
}

/** The entries that show in a window, earliest first, longest first on a tie. */
export function entriesInWindow<T extends Span>(entries: Iterable<T>, window: Span): T[] {
  return [...entries]
    .filter((e) => overlaps(e, window))
    .sort((a, b) => a.startMs - b.startMs || b.endMs - a.endMs);
}

/** The part of a span that falls inside a window, or null when none of it does. */
export function clip(span: Span, window: Span): Span | null {
  if (!overlaps(span, window)) return null;
  return { startMs: Math.max(span.startMs, window.startMs), endMs: Math.min(span.endMs, window.endMs) };
}

/**
 * Whether a span swallows a whole day. Those are drawn in the band above the
 * hour grid instead of as a block inside it: a week away is one fact, and
 * drawing it as five full-height columns buries every meeting under it.
 */
export function coversWholeDay(span: Span, day: Span): boolean {
  return span.startMs <= day.startMs && span.endMs >= day.endMs;
}

/** An entry, told where to draw itself: lane `lane` of `lanes` side by side. */
export interface Placed<T> {
  item: T;
  lane: number;
  lanes: number;
}

/**
 * Sit overlapping spans side by side, the way a calendar does.
 *
 * Lanes are counted per CLUSTER — a run of spans connected by overlap — not
 * across the whole day. Otherwise one person's all-morning block would squeeze
 * every unrelated afternoon block into a third of a column for no reason.
 */
export function packLanes<T extends Span>(items: T[]): Placed<T>[] {
  const sorted = [...items].sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
  const out: Placed<T>[] = [];

  // Spans placed since the last gap, and when each lane last ended.
  let cluster: Placed<T>[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;

  const closeCluster = (): void => {
    for (const placed of cluster) placed.lanes = laneEnds.length;
    out.push(...cluster);
    cluster = [];
    laneEnds = [];
  };

  for (const item of sorted) {
    // A span starting at or after everything so far begins a fresh cluster.
    if (item.startMs >= clusterEnd) closeCluster();

    let lane = laneEnds.findIndex((end) => end <= item.startMs);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.endMs);
    } else {
      laneEnds[lane] = item.endMs;
    }
    cluster.push({ item, lane, lanes: laneEnds.length });
    clusterEnd = Math.max(clusterEnd, item.endMs);
  }
  closeCluster();
  return out;
}

/**
 * Where a span sits inside a day, as two fractions of that day's height.
 *
 * Proportional to the day's REAL length, which is 23 or 25 hours on the two
 * days a year the clocks move. Fixing the grid at 24 rows and stretching the
 * day across them keeps the hour lines level across the week — the thing the
 * eye actually uses — at the cost of the labels being out by up to an hour
 * through the middle of those two days. The alternative, a column one row
 * taller than its neighbours twice a year, is worse every other day.
 */
export function dayFractions(span: Span, day: Span): { top: number; height: number } | null {
  const inside = clip(span, day);
  if (!inside) return null;
  const length = day.endMs - day.startMs;
  return {
    top: (inside.startMs - day.startMs) / length,
    height: (inside.endMs - inside.startMs) / length,
  };
}

/**
 * Where an instant sits inside a day, as a fraction of its height, or null when
 * it falls on another day. Half-open, so midnight belongs to the day it starts.
 */
export function dayFraction(ms: number, day: Span): number | null {
  if (ms < day.startMs || ms >= day.endMs) return null;
  return (ms - day.startMs) / (day.endMs - day.startMs);
}
